/**
 * Shape Geometry Engine (template_engine / editor preview)
 * ========================================================
 *
 * Shape-specific capabilities the reused Animation/Motion/Effects engines
 * cannot provide: vector geometry generation, deformations, border/stroke
 * mechanics (dash, marching ants, draw-on / path-trim, multiple outlines),
 * a shape repeater, and shape-to-shape morph.
 *
 * Mirror of video_worker/py_server/app/utils/shape_engine.py — keep the math
 * IDENTICAL so the editor preview matches the rendered video.
 *
 * Core idea: every shape is reduced to one or more closed polylines (list of
 * {x,y} points) in ABSOLUTE canvas coordinates. The renderer then fills/strokes
 * those polylines. Deformations warp the point list; the repeater produces
 * several transformed copies; morph interpolates between two point sets.
 *
 * Config lives on the item:
 *   item.shapeDeform = { type, amount, axis, animate, speed, phase, seed }
 *   item.shapeBorder = { dash:[on,off], dashOffset, marchingAnts, marchSpeed,
 *                        trimStart, trimEnd, drawOn, drawSpeed,
 *                        outlines:[{color,width,align}], align }
 *   item.shapeRepeat = { mode:'grid'|'circle'|'line', count, cols, spacingX,
 *                        spacingY, radius, startAngle, rotateEach, scaleEach }
 *   item.shapeMorph  = { target: shapeType, progress (0..1), animate, duration }
 *   Shape params: item.sides (polygon/star), item.innerRadius (star 0..1),
 *                 item.cornerRadius / borderRadius (rect), item.points (custom)
 *
 * Entry points:
 *   buildShapeGeometry(item, t) -> { subpaths: [[{x,y}...]], closed, meta }
 *   isDeformType(type), deformTypes()
 */

const TAU = Math.PI * 2
const clamp01 = (v) => Math.max(0, Math.min(1, v))
const num = (v, d) => (typeof v === 'number' && !Number.isNaN(v) ? v : d)
const lerp = (a, b, t) => a + (b - a) * t

/** Deterministic pseudo-random in [-1,1] — shared constant with the other engines. */
const sNoise = (step, seed) => {
  const x = Math.sin((step + 1) * 127.1 + (seed || 0) * 311.7) * 43758.5453
  return (x - Math.floor(x)) * 2 - 1
}

// ---------------------------------------------------------------------------
// Base geometry — produce a closed polyline in a unit box [0..1]x[0..1].
// Each generator returns an array of {x,y} (normalized). Resolution is high
// enough that deformations look smooth.
// ---------------------------------------------------------------------------

const RES = 96 // samples around curved shapes / per side after resampling

const ringPoints = (n, cx, cy, rx, ry, rot = -Math.PI / 2) => {
  const pts = []
  for (let i = 0; i < n; i++) {
    const a = rot + (i / n) * TAU
    pts.push({ x: cx + Math.cos(a) * rx, y: cy + Math.sin(a) * ry })
  }
  return pts
}

/** Resample a closed polyline to exactly `count` points by arc length. */
const resampleClosed = (pts, count) => {
  if (pts.length < 2) return pts
  // cumulative perimeter
  const seg = []
  let total = 0
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i]
    const b = pts[(i + 1) % pts.length]
    const d = Math.hypot(b.x - a.x, b.y - a.y)
    seg.push(d)
    total += d
  }
  if (total === 0) return pts
  const out = []
  const step = total / count
  let target = 0
  let i = 0
  let acc = 0
  for (let k = 0; k < count; k++) {
    while (i < pts.length && acc + seg[i] < target) { acc += seg[i]; i++ }
    const a = pts[i % pts.length]
    const b = pts[(i + 1) % pts.length]
    const segLen = seg[i % pts.length] || 1
    const f = (target - acc) / segLen
    out.push({ x: lerp(a.x, b.x, f), y: lerp(a.y, b.y, f) })
    target += step
  }
  return out
}

/** Rounded-rect outline in unit box; r is normalized (0..0.5). */
const roundedRectPoints = (r) => {
  const rr = Math.max(0, Math.min(0.5, r))
  if (rr <= 0.0001) {
    // sharp corners — densify edges so deformations have vertices to move
    const pts = []
    const per = RES / 4
    const corners = [[0, 0], [1, 0], [1, 1], [0, 1]]
    for (let c = 0; c < 4; c++) {
      const a = corners[c]; const b = corners[(c + 1) % 4]
      for (let i = 0; i < per; i++) pts.push({ x: lerp(a[0], b[0], i / per), y: lerp(a[1], b[1], i / per) })
    }
    return pts
  }
  const pts = []
  const arc = RES / 4 / 2
  // corners: center, startAngle
  const cs = [
    { cx: 1 - rr, cy: rr, a0: -Math.PI / 2 },
    { cx: 1 - rr, cy: 1 - rr, a0: 0 },
    { cx: rr, cy: 1 - rr, a0: Math.PI / 2 },
    { cx: rr, cy: rr, a0: Math.PI },
  ]
  for (let c = 0; c < 4; c++) {
    const { cx, cy, a0 } = cs[c]
    for (let i = 0; i <= arc; i++) {
      const a = a0 + (i / arc) * (Math.PI / 2)
      pts.push({ x: cx + Math.cos(a) * rr, y: cy + Math.sin(a) * rr })
    }
  }
  return pts
}

/** Build the normalized base outline for a shapeType. Returns {pts, closed}. */
const baseGeometry = (shapeType, item) => {
  const st = shapeType || 'rectangle'
  switch (st) {
    case 'rectangle':
    case 'square': {
      const w = Math.max(1, item.width || 1)
      const h = Math.max(1, item.height || 1)
      const rPx = num(item.cornerRadius, num(item.borderRadius, 0))
      const rNorm = rPx > 0 ? rPx / Math.min(w, h) : 0
      return { pts: roundedRectPoints(rNorm), closed: true }
    }
    case 'circle':
    case 'ellipse':
      return { pts: ringPoints(RES, 0.5, 0.5, 0.5, 0.5), closed: true }
    case 'triangle':
      return { pts: resampleClosed([{ x: 0.5, y: 0 }, { x: 1, y: 1 }, { x: 0, y: 1 }], RES), closed: true }
    case 'diamond':
      return { pts: resampleClosed([{ x: 0.5, y: 0 }, { x: 1, y: 0.5 }, { x: 0.5, y: 1 }, { x: 0, y: 0.5 }], RES), closed: true }
    case 'pentagon':
      return { pts: resampleClosed(ringPoints(5, 0.5, 0.5, 0.5, 0.5), RES), closed: true }
    case 'hexagon':
      return { pts: resampleClosed(ringPoints(6, 0.5, 0.5, 0.5, 0.5, 0), RES), closed: true }
    case 'polygon': {
      const sides = Math.max(3, Math.round(num(item.sides, 6)))
      return { pts: resampleClosed(ringPoints(sides, 0.5, 0.5, 0.5, 0.5), RES), closed: true }
    }
    case 'star': {
      const sides = Math.max(3, Math.round(num(item.sides, 5)))
      const inner = clamp01(num(item.innerRadius, 0.4))
      const raw = []
      for (let i = 0; i < sides * 2; i++) {
        const a = -Math.PI / 2 + (i / (sides * 2)) * TAU
        const rr = (i % 2 === 0) ? 0.5 : 0.5 * inner
        raw.push({ x: 0.5 + Math.cos(a) * rr, y: 0.5 + Math.sin(a) * rr })
      }
      return { pts: resampleClosed(raw, RES), closed: true }
    }
    case 'arrow': {
      const raw = [
        { x: 0, y: 0.33 }, { x: 0.6, y: 0.33 }, { x: 0.6, y: 0.1 },
        { x: 1, y: 0.5 }, { x: 0.6, y: 0.9 }, { x: 0.6, y: 0.67 }, { x: 0, y: 0.67 },
      ]
      return { pts: resampleClosed(raw, RES), closed: true }
    }
    case 'line':
      return { pts: [{ x: 0, y: 0.5 }, { x: 1, y: 0.5 }], closed: false }
    default: {
      // Unknown — rectangle fallback.
      return { pts: roundedRectPoints(0), closed: true }
    }
  }
}

// ---------------------------------------------------------------------------
// Deformations — warp a normalized point list in place (0..1 space).
// Signature: (pts, cfg, t01) where t01 is an animation phase 0..1 (or static).
// ---------------------------------------------------------------------------

const DEFORMS = {}
const registerDeform = (type, fn) => { DEFORMS[type] = fn }
export const isDeformType = (type) => Object.prototype.hasOwnProperty.call(DEFORMS, type)
export const deformTypes = () => Object.keys(DEFORMS)

// Taper: narrow one end (x) while widening the other, around the vertical center.
registerDeform('taper', (pts, cfg) => {
  const amt = num(cfg.amount, 0.5)
  return pts.map((p) => {
    const t = p.x // 0..1 along x
    const scale = lerp(1, Math.max(0.01, 1 - amt), t)
    return { x: p.x, y: 0.5 + (p.y - 0.5) * scale }
  })
})

// Twist: rotate points around center by an angle that varies with radius.
registerDeform('twist', (pts, cfg) => {
  const amt = num(cfg.amount, 1) * Math.PI // radians at the rim
  return pts.map((p) => {
    const dx = p.x - 0.5, dy = p.y - 0.5
    const r = Math.hypot(dx, dy) / 0.70710678
    const a = Math.atan2(dy, dx) + amt * r
    const rr = Math.hypot(dx, dy)
    return { x: 0.5 + Math.cos(a) * rr, y: 0.5 + Math.sin(a) * rr }
  })
})

// Bend: curve a shape along an arc (shift y by a parabola of x).
registerDeform('bend', (pts, cfg) => {
  const amt = num(cfg.amount, 0.3)
  return pts.map((p) => {
    const t = p.x - 0.5
    return { x: p.x, y: p.y - amt * (0.25 - t * t) }
  })
})

// Inflate / Deflate: push points out from (or in toward) the center.
registerDeform('inflate', (pts, cfg) => {
  const amt = num(cfg.amount, 0.3)
  return pts.map((p) => {
    const dx = p.x - 0.5, dy = p.y - 0.5
    const r = Math.hypot(dx, dy)
    const bulge = Math.sin(r * Math.PI) * amt
    const k = r > 0 ? (r + bulge) / r : 1
    return { x: 0.5 + dx * k, y: 0.5 + dy * k }
  })
})

// Wave: travelling sinusoidal displacement perpendicular to the axis.
registerDeform('wave', (pts, cfg, t01) => {
  const amt = num(cfg.amount, 0.1)
  const freq = num(cfg.frequency, 3)
  const phase = num(cfg.phase, 0) + (t01 != null ? t01 * TAU : 0)
  const axis = cfg.axis || 'x'
  return pts.map((p) => {
    if (axis === 'y') return { x: p.x + Math.sin(p.y * freq * TAU + phase) * amt, y: p.y }
    return { x: p.x, y: p.y + Math.sin(p.x * freq * TAU + phase) * amt }
  })
})

// Pinch / Bulge: radial distortion that pinches (amt<0) or bulges (amt>0) center.
registerDeform('pinch', (pts, cfg) => {
  const amt = num(cfg.amount, 0.4)
  return pts.map((p) => {
    const dx = p.x - 0.5, dy = p.y - 0.5
    const r = Math.hypot(dx, dy) / 0.70710678
    const k = 1 - amt * (1 - r)
    return { x: 0.5 + dx * k, y: 0.5 + dy * k }
  })
})

// Ripple: concentric sinusoidal waves in the radial direction.
registerDeform('ripple', (pts, cfg, t01) => {
  const amt = num(cfg.amount, 0.06)
  const freq = num(cfg.frequency, 5)
  const phase = num(cfg.phase, 0) + (t01 != null ? t01 * TAU : 0)
  return pts.map((p) => {
    const dx = p.x - 0.5, dy = p.y - 0.5
    const r = Math.hypot(dx, dy)
    const disp = Math.sin(r * freq * TAU - phase) * amt
    const k = r > 0 ? (r + disp) / r : 1
    return { x: 0.5 + dx * k, y: 0.5 + dy * k }
  })
})

// ---------------------------------------------------------------------------
// Morph — interpolate between two normalized point sets (same count).
// ---------------------------------------------------------------------------

const morphGeometry = (fromPts, item, progress) => {
  const target = item.shapeMorph && item.shapeMorph.target
  if (!target) return fromPts
  const to = baseGeometry(target, item)
  const n = Math.max(fromPts.length, to.pts.length)
  const a = resampleClosed(fromPts, n)
  const b = resampleClosed(to.pts, n)
  const p = clamp01(progress)
  return a.map((pt, i) => ({ x: lerp(pt.x, b[i].x, p), y: lerp(pt.y, b[i].y, p) }))
}

// ---------------------------------------------------------------------------
// Timing: resolve an animation phase for deform/trim/morph.
// ---------------------------------------------------------------------------

const elapsed = (item, t) => {
  const it = item.time || { start: 0, duration: 2 }
  return Math.max(0, (t || 0) - (it.start || 0))
}

// ---------------------------------------------------------------------------
// Main entry: build absolute-coordinate geometry for the item at time t.
// ---------------------------------------------------------------------------

/**
 * @returns {{ subpaths: Array<Array<{x:number,y:number}>>, closed: boolean, border: object, meta: object }}
 *   subpaths are ABSOLUTE canvas points (one per repeater copy).
 */
export const buildShapeGeometry = (item, t = 0) => {
  const w = Math.max(1, item.width || 1)
  const h = Math.max(1, item.height || 1)
  const x0 = item.x || 0
  const y0 = item.y || 0

  // 1. Base normalized geometry.
  let { pts, closed } = baseGeometry(item.shapeType, item)

  // 2. Morph (interpolate toward target shape).
  if (item.shapeMorph && item.shapeMorph.target) {
    let p = num(item.shapeMorph.progress, 0)
    if (item.shapeMorph.animate) {
      const e = elapsed(item, t)
      p = clamp01(e / Math.max(0.0001, num(item.shapeMorph.duration, 1)))
    }
    pts = morphGeometry(pts, item, p)
  }

  // 3. Deformation.
  const def = item.shapeDeform
  if (def && def.type && DEFORMS[def.type]) {
    let t01 = null
    if (def.animate) {
      const e = elapsed(item, t)
      t01 = (e * num(def.speed, 0.5)) % 1
    }
    try { pts = DEFORMS[def.type](pts, def, t01) } catch (e) { /* ignore */ }
  }

  // 4. Scale normalized -> absolute.
  const toAbs = (plist) => plist.map((p) => ({ x: x0 + p.x * w, y: y0 + p.y * h }))

  // 5. Repeater — produce N transformed copies.
  const subpaths = []
  const rep = item.shapeRepeat
  if (rep && rep.mode && num(rep.count, 1) > 1) {
    const count = Math.max(1, Math.round(rep.count))
    const cx = x0 + w / 2, cy = y0 + h / 2
    for (let i = 0; i < count; i++) {
      let ox = 0, oy = 0, rot = 0, sc = 1
      if (rep.mode === 'grid') {
        const cols = Math.max(1, Math.round(num(rep.cols, Math.ceil(Math.sqrt(count)))))
        const col = i % cols, row = Math.floor(i / cols)
        ox = col * num(rep.spacingX, w + 10)
        oy = row * num(rep.spacingY, h + 10)
      } else if (rep.mode === 'line') {
        ox = i * num(rep.spacingX, w + 10)
        oy = i * num(rep.spacingY, 0)
      } else if (rep.mode === 'circle') {
        const a = num(rep.startAngle, 0) * Math.PI / 180 + (i / count) * TAU
        const rad = num(rep.radius, Math.max(w, h))
        ox = Math.cos(a) * rad
        oy = Math.sin(a) * rad
      }
      rot = num(rep.rotateEach, 0) * i * Math.PI / 180
      sc = Math.pow(num(rep.scaleEach, 1), i)
      const copy = pts.map((p) => {
        // normalized -> absolute around the shape center, then transform
        let ax = x0 + p.x * w, ay = y0 + p.y * h
        // rotate+scale around the copy's own center
        let rx = ax - cx, ry = ay - cy
        const ca = Math.cos(rot), sa = Math.sin(rot)
        const nx = (rx * ca - ry * sa) * sc, ny = (rx * sa + ry * ca) * sc
        return { x: cx + nx + ox, y: cy + ny + oy }
      })
      subpaths.push(copy)
    }
  } else {
    subpaths.push(toAbs(pts))
  }

  // 6. Border config resolution (dash offset / draw-on progress animated here).
  const border = resolveBorder(item, t)

  return { subpaths, closed, border, meta: { shapeType: item.shapeType } }
}

/** Resolve the border/stroke mechanics config, animating dash offset / trim. */
const resolveBorder = (item, t) => {
  const b = item.shapeBorder
  if (!b) return null
  const out = {
    dash: Array.isArray(b.dash) ? b.dash : null,
    dashOffset: num(b.dashOffset, 0),
    outlines: Array.isArray(b.outlines) ? b.outlines : null,
    align: b.align || 'center',
    trimStart: clamp01(num(b.trimStart, 0)),
    trimEnd: clamp01(num(b.trimEnd, 1)),
  }
  // Marching ants — animate the dash offset over time.
  if (b.marchingAnts) {
    const e = elapsed(item, t)
    out.dash = out.dash || [6, 6]
    out.dashOffset = (out.dashOffset + e * num(b.marchSpeed, 30)) % 1000
  }
  // Draw-on — animate trimEnd from 0 to 1 over drawSpeed.
  if (b.drawOn) {
    const e = elapsed(item, t)
    out.trimEnd = clamp01(e * num(b.drawSpeed, 0.5))
  }
  return out
}

/** Build an SVG path 'd' string from absolute points. */
export const pointsToSvgPath = (pts, closed) => {
  if (!pts || pts.length === 0) return ''
  let d = `M ${pts[0].x.toFixed(2)} ${pts[0].y.toFixed(2)}`
  for (let i = 1; i < pts.length; i++) d += ` L ${pts[i].x.toFixed(2)} ${pts[i].y.toFixed(2)}`
  if (closed) d += ' Z'
  return d
}

/** True if the item needs the geometry-engine render path (vs simple div/SVG). */
export const shapeNeedsEngine = (item) => {
  if (!item) return false
  return !!(
    (item.shapeDeform && item.shapeDeform.type) ||
    item.shapeBorder ||
    (item.shapeRepeat && item.shapeRepeat.mode && num(item.shapeRepeat.count, 1) > 1) ||
    (item.shapeMorph && item.shapeMorph.target) ||
    (item.shapeType === 'polygon' && item.sides) ||
    (item.shapeType === 'star' && (item.sides || item.innerRadius != null))
  )
}

export default { buildShapeGeometry, pointsToSvgPath, shapeNeedsEngine, isDeformType, deformTypes }
